import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cx } from '@/lib/cx';
import { Icon } from '@/ui';
import { usePersistentSet } from '@/lib/prefs';
import { NAV, findNavItem } from './navigation';
import { prefetchScreen } from './screens';
import { usePermissions } from './permissions-context';

/**
 * Primary navigation.
 *
 * Groups collapse, and the group owning the current screen is expanded on
 * arrival — an office user works inside one group for a whole shift, so the
 * other five should be out of the way without being hard to reach.
 *
 * Which groups the user has opened is remembered across reloads. It is a
 * preference rather than screen state, so it is stored locally and not put in
 * the URL — nobody wants their sidebar arrangement travelling inside a link
 * they send a colleague.
 *
 * The active item is marked with a left line in `accent`, not a filled pill.
 * A fill would be the sixth solid colour on screen; a line reads as "you are
 * on this line of the register" and costs nothing visually.
 */
export function Sidebar({
  collapsed,
  onToggleCollapsed,
  onSignOut,
}: {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onSignOut: () => void;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const { hasAny } = usePermissions();

  const activeItem = findNavItem(location.pathname);
  const activeGroup = NAV.find((g) => g.items.some((i) => i.id === activeItem?.id));
  const [expanded, toggle] = usePersistentSet('sidebar.expanded', ['core']);

  /* Arriving in a group opens it — including on the first render, which is what
     makes a reloaded or pasted deep link show its own nav entry rather than
     leaving the highlight hidden inside a collapsed group.

     Keyed on the group *changing*, so it fires on arrival and not on every
     render. That is the difference between "opens when you get there" and
     "cannot be closed while you are there": collapsing the group you are in
     leaves `prevGroup` untouched, so nothing reopens it. */
  const [prevGroup, setPrevGroup] = useState<string | null>(null);
  if (activeGroup && prevGroup !== activeGroup.id) {
    setPrevGroup(activeGroup.id);
    if (!expanded.has(activeGroup.id)) toggle(activeGroup.id);
  }

  return (
    <nav
      className={cx(
        'flex shrink-0 flex-col border-r border-line bg-surface transition-[width] duration-150',
        collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
      )}
      aria-label="Main"
    >
      {/* v1 `.sidebar`: 12px vertical / 8px horizontal padding. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
        {NAV.map((group) => {
          const visible = group.items.filter((item) => !item.hidden && hasAny(item.permissions));
          if (visible.length === 0) return null;
          const isOpen = collapsed || expanded.has(group.id);

          return (
            // v1 `.sidebar__group + .sidebar__group`: separated by a hairline
            // with 12px of air either side, so the six groups read as sections
            // rather than as one long list.
            <div
              key={group.id}
              className={cx(
                'flex flex-col gap-0.5',
                '[&:not(:first-child)]:mt-3 [&:not(:first-child)]:border-t [&:not(:first-child)]:border-line-soft [&:not(:first-child)]:pt-3',
                collapsed && '[&:not(:first-child)]:mt-2 [&:not(:first-child)]:pt-2',
              )}
            >
              {!collapsed && (
                <button
                  type="button"
                  onClick={() => toggle(group.id)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between px-3 pb-1.5 pt-1 text-2xs font-semibold uppercase tracking-caps-wide text-muted hover:text-ink"
                >
                  <span>{t(group.labelKey)}</span>
                  <Icon
                    name="chevronDown"
                    size={11}
                    className={cx('transition-transform duration-150', !isOpen && '-rotate-90')}
                  />
                </button>
              )}

              {isOpen && (
                <ul>
                  {visible.map((item) => (
                    <li key={item.id}>
                      <NavLink
                        to={`/${item.path}`}
                        title={collapsed ? t(item.labelKey) : undefined}
                        /* Start the screen's chunk on approach, not on click.
                           The pointer is over the item for a few hundred
                           milliseconds before the button goes down, and that is
                           long enough for a chunk this size — so the click
                           usually has nothing left to wait for. `onFocus`
                           covers arriving by Tab, where there is no hover at
                           all. Repeat calls are free; see prefetchScreen. */
                        onPointerEnter={() => prefetchScreen(item.id)}
                        onFocus={() => prefetchScreen(item.id)}
                        className={({ isActive }) =>
                          cx(
                            'flex items-center gap-2.5 rounded-item py-2 text-base transition-colors duration-150',
                            collapsed ? 'justify-center px-0' : 'px-3',
                            isActive
                              ? // v1 tinted the icon with the label when active.
                                'bg-accent-soft font-semibold text-accent'
                              : // …and left it a step lighter than the label otherwise.
                                'font-medium text-ink hover:bg-nav-hover [&>svg]:text-muted',
                          )
                        }
                      >
                        <Icon name={item.icon} size={20} />
                        {!collapsed && <span className="truncate">{t(item.labelKey)}</span>}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* v1's footer: a full-width Logout that turns red on hover, and a small
          unlabelled collapse toggle centred beneath it. */}
      <div className="shrink-0 px-2 pb-2">
        <button
          type="button"
          onClick={onSignOut}
          className={cx(
            'flex w-full items-center gap-2.5 rounded-item px-3 py-2 text-base font-medium text-faint transition-colors hover:bg-danger-soft hover:text-danger',
            collapsed && 'justify-center px-0',
          )}
        >
          <Icon name="logout" size={20} />
          {!collapsed && <span>{t('nav.logout')}</span>}
        </button>

        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="mt-2 flex w-full items-center justify-center rounded-item p-2 text-faint transition-colors hover:bg-nav-hover hover:text-muted"
        >
          <Icon name={collapsed ? 'panelRight' : 'panelLeft'} size={18} />
        </button>
      </div>
    </nav>
  );
}
